import StorageFactory from "./StorageFactory";
import {
  SESSION_LOGIN_NAME_KEY,
  SESSION_USER_COUNTRY_CODE_KEY,
  SESSION_USER_ID_KEY,
  SESSION_USER_MOBILE_NO_KEY,
  SESSION_USER_NAME_KEY,
  SESSION_USER_TYPE_KEY,
} from "./VMConstants";
import { checkIsPermissionExist, isAdminLoggedIn } from "./UtilHelper";

const getSessionValue = (key) => {
  try {
    return new StorageFactory("session").get(key);
  } catch {
    return null;
  }
};

export const getSessionUserId = () => getSessionValue(SESSION_USER_ID_KEY);

export const getSessionUserType = () => getSessionValue(SESSION_USER_TYPE_KEY);

export const isSessionAdmin = () => isAdminLoggedIn(getSessionUserType());

const asId = (value) => {
  if (value == null || value === "") return null;
  if (typeof value === "object") return null;
  return String(value);
};

/**
 * Resolve the creator user id from a record. Entities may expose the owner as
 * `created_by` (id or `{ user_id }`), `created_by_id`, or similar.
 */
export const getRecordCreatorId = (record) => {
  if (record == null || typeof record !== "object") return null;

  const candidates = [
    record.created_by,
    record.createdBy,
    record.created_by_id,
    record.createdById,
    record.created_user_id,
    record.added_by,
    record.owner_id,
  ];

  for (const candidate of candidates) {
    if (candidate == null || candidate === "") continue;
    if (typeof candidate === "object") {
      const nested = asId(
        candidate.user_id ??
          candidate.userId ??
          candidate.created_by ??
          candidate._id ??
          candidate.id
      );
      if (nested) return nested;
      continue;
    }
    const id = asId(candidate);
    if (id) return id;
  }

  return null;
};

const getCreatorObject = (record) => {
  const creator = record?.created_by ?? record?.createdBy;
  return creator && typeof creator === "object" ? creator : null;
};

const normalizeName = (value) => String(value || "").trim().toLowerCase();

const normalizePhone = (countryCode, mobile) => {
  const digits = `${String(countryCode || "").replace(/\D/g, "")}${String(
    mobile || ""
  ).replace(/\D/g, "")}`;
  return digits.replace(/^0+/, "");
};

export const isRecordOwner = (record, currentUserId = getSessionUserId()) => {
  if (record == null) return false;

  const creatorId = getRecordCreatorId(record);
  if (
    creatorId &&
    currentUserId != null &&
    currentUserId !== "" &&
    String(creatorId) === String(currentUserId)
  ) {
    return true;
  }

  const creator = getCreatorObject(record);
  if (!creator) return false;

  const loginName = normalizeName(getSessionValue(SESSION_LOGIN_NAME_KEY));
  const creatorEmail = normalizeName(creator.email);
  if (creatorEmail && loginName && creatorEmail === loginName) {
    return true;
  }

  const sessionName = normalizeName(getSessionValue(SESSION_USER_NAME_KEY));
  const creatorName = normalizeName(creator.name);
  if (sessionName && creatorName && sessionName === creatorName) {
    return true;
  }

  const sessionPhone = normalizePhone(
    getSessionValue(SESSION_USER_COUNTRY_CODE_KEY),
    getSessionValue(SESSION_USER_MOBILE_NO_KEY)
  );
  const creatorPhone = normalizePhone(creator.country_code, creator.mobile_no);
  if (sessionPhone && creatorPhone && sessionPhone === creatorPhone) {
    return true;
  }

  return false;
};

/**
 * Existing permission check AND (Admin OR record owner).
 * Ownership never grants a permission the user does not already have.
 *
 * @param {string[]|null} permissions session/redux permission ids
 * @param {string|null} permissionId VM permission required for the action; omit only when the screen has no permission id
 * @param {object} record row/entity that may include created_by
 */
export const canMutateRecord = (permissions, permissionId, record) => {
  if (permissionId && !checkIsPermissionExist(permissions, permissionId)) {
    return false;
  }
  if (isSessionAdmin()) return true;
  return isRecordOwner(record);
};

export const RECORD_OWNERSHIP_DENIED_MESSAGE =
  "You can only edit or delete records you created.";
